import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';
import { useTerminology } from '@/lib/useTerminology';
import { ablageflaeche, darfAblegen, usePosteingang } from '@/components/eingang/posteingang';
import { adressAblageflaeche, darfAdresseAblegen, useAdressAblage } from '@/lib/adress-ablage';
import { FreigabenZahl } from './FreigabenZahl';
import { PosteingangZahl } from '@/components/eingang/PosteingangSymbol';

/**
 * Seitenleiste des ERP — Einträge, Sichtbarkeit und die Gruppen mit Untermenü.
 *
 * Aus `AppLayout.tsx` herausgelöst (AP-40 Phase A), damit die Menüführung ohne das
 * ganze Layout (Branding, Update-Hinweis, Anmeldung) prüfbar ist.
 */

/**
 * Kontext für den geführten Datenweg (AP-Menü-Neuordnung).
 * Steuert, welche Einrichtungs-Punkte sichtbar sind — IMMER zusätzlich zur
 * Rollenprüfung (UND-Verknüpfung), nie als Ersatz dafür.
 */
export interface NavVisibilityCtx {
  /** Projekte/Tätigkeiten werden in Symphenia selbst gepflegt */
  usesManual: boolean;
  /** Mindestens ein Bereich kommt aus einem Fremdsystem (Synchronisation) */
  usesExternal: boolean;
  /** Stempeln erfordert einen QR-/Barcode-Scan vor Ort */
  usesQr: boolean;
  /** Eigene Zusatzfelder werden erfasst (Wizard-Schalter bzw. Felder existieren) */
  usesFields: boolean;
}

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * AP-30-D — Das Recht, das diesen Eintrag sichtbar macht.
   *
   * Genau **eines**, nicht eine Liste: Eine Rollen-Allowlist war nötig, weil
   * eine Leiter „MANAGER oder READONLY" nicht ausdrücken kann. Ein Recht kann
   * das — und wo hier doch mehrere stünden, wäre der Eintrag in Wahrheit zwei.
   *
   * **Gruppen tragen keines.** Sie erscheinen, wenn mindestens ein Kind
   * erscheint (`NavGroup` gibt sonst `null` zurück). Ein Gate am Elternteil war
   * eine zweite Wahrheit: Es musste jedes Mal mitgepflegt werden, wenn ein Kind
   * weiter oder enger wurde — und wenn nicht, verschwand ein erreichbarer
   * Eintrag hinter einem geschlossenen Ast.
   */
  permission?: string;
  /** AP-11-A: Modul-Slug (z.B. "CRM") — Nav-Eintrag wird ausgeblendet wenn Modul nicht gebucht */
  requiredModule?: string;
  /**
   * AP-29-E: Nur für den Betreiber-Mandanten. Ein Kunde soll nicht einmal
   * sehen, dass es die Konsole gibt — auch sein SUPER_ADMIN nicht.
   */
  operatorOnly?: boolean;
  /** Zusätzlicher Sichtbarkeits-Filter abhängig vom gewählten Datenweg (UND mit roles) */
  visibleWhen?: (ctx: NavVisibilityCtx) => boolean;
  /**
   * AP-40 — Ein Hauptmenüpunkt mit Startseite. `to` ist dann der Pfad seines Dashboards.
   *
   * - `true`: Das Dashboard ist gebaut. Klick auf Symbol oder Namen öffnet es und blendet
   *   das Untermenü ein; der Pfeil blendet nur ein und aus.
   * - `'folgt'`: Übergang (MD-12) — noch kein Dashboard; der Name führt zum ersten
   *   sichtbaren Unterpunkt. So kann die Menüführung vor den Dashboards kommen.
   * - ohne Angabe: Einrichtungsgruppe (Einstellungen, Integrationen, Betreiber-Konsole,
   *   „Einrichtung" der Zeiterfassung) — der Name blendet ein und aus wie bisher (MD-1).
   */
  dashboard?: true | 'folgt';
  /** AP-39 12.1 — Menüpunkt „Posteingang": trägt den Zähler und nimmt gezogene Dateien an (PE-5). */
  posteingang?: true;
  /** Menüpunkt „Geschäftspartner": nimmt eine gezogene Internetadresse an und reicht sie an die Seite (`lib/adress-ablage`). */
  adressAblage?: true;
  /** AP-34 11.5 — Menüpunkt „Meine Freigaben": trägt den Zähler der offenen Freigabeaufgaben. */
  freigaben?: true;
  /** AP-18 Nachtrag 1 — Menüpunkt „Hilfe": nur, wenn das Inhaltsverzeichnis des Benutzers nicht leer ist. */
  hilfe?: true;
  children?: NavItem[];
}

// ─── Helpers ──────────────────────────────────────────────────────

/** Fail-open-Default: solange die Config nicht geladen ist, alles zeigen. */
export const NAV_CTX_DEFAULT: NavVisibilityCtx = { usesManual: true, usesExternal: true, usesQr: true, usesFields: true };

/**
 * AP-11-A / AP-30-D: Prüft, ob ein Nav-Eintrag angezeigt werden soll.
 *
 * Alle Prüfungen müssen bestehen: **Recht** UND gebuchtes Modul UND (falls
 * gesetzt) Datenweg. `visibleWhen` ist ein ZUSÄTZLICHER Filter — das Recht
 * bleibt die maßgebliche Schranke und wird hier nie umgangen.
 *
 * Und auch hier: Ausblenden ist Bequemlichkeit, keine Sicherheitsgrenze. Die
 * trägt der `PermissionGuard`.
 */
export function isVisible(
  item: NavItem,
  darf: (p: string) => boolean,
  hasModule: (m: string) => boolean,
  navCfg: NavVisibilityCtx = NAV_CTX_DEFAULT,
  isOperator = false,
): boolean {
  // AP-29-E: Die Betreiber-Konsole ist für Kunden unsichtbar — auch für deren
  // SUPER_ADMIN. Die Sperre selbst sitzt im Backend (PlatformAdminGuard); hier
  // geht es darum, niemandem einen Menüpunkt anzubieten, der 403 liefert.
  if (item.operatorOnly && !isOperator) return false;
  if (item.permission && !darf(item.permission)) return false;
  if (item.requiredModule && !hasModule(item.requiredModule)) return false;
  if (item.visibleWhen && !item.visibleWhen(navCfg)) return false;
  return true;
}

/**
 * Pfade, unter denen ein Bereichs-Dashboard liegt (AP-40 5.). Auf einem davon ist kein
 * Unterpunkt aktiv — auch nicht die Liste, deren Pfad der Anfang des Dashboard-Pfads ist
 * (`/calculations` vor `/calculations/dashboard`). Ohne diese Ausnahme leuchtete die Liste
 * auf dem Dashboard mit.
 */
const DASHBOARD_PFADE = new Set([
  '/articles/dashboard',
  '/project-management',
  '/calculations/dashboard',
  '/correspondence/dashboard',
  '/time-tracking/dashboard',
  '/payroll/dashboard',
  '/incoming-invoices/dashboard',
  '/site-docs/dashboard',
]);

/** Exakt-Match-Routen, sonst Prefix-Match. */
export function matchesPath(to: string, pathname: string): boolean {
  if (DASHBOARD_PFADE.has(pathname)) return to === pathname;
  if (to === '/time-tracking') return pathname === '/time-tracking';
  if (to === '/settings') return pathname === '/settings';
  // AP-34: „Rechnungen" bleibt auf einer geöffneten Rechnung aktiv, nicht aber in den anderen Unterpunkten.
  if (to === '/incoming-invoices')
    return pathname === '/incoming-invoices' || (pathname.startsWith('/incoming-invoices/') && !['/incoming-invoices/inbox', '/incoming-invoices/approvals', '/incoming-invoices/settings', '/incoming-invoices/open-items', '/incoming-invoices/payment-runs', '/incoming-invoices/handover', '/incoming-invoices/overhead', '/incoming-invoices/dashboard'].includes(pathname));
  // Die Liste ist nicht aktiv, während man die Stammlisten oder die Einstellungen ansieht.
  if (to === '/articles')
    return (
      pathname === '/articles' ||
      (pathname.startsWith('/articles/') && pathname !== '/articles/stammlisten' && pathname !== '/articles/einstellungen')
    );
  // AP-32: „Belege" bleibt auf einem geöffneten Beleg aktiv, nicht aber in Mahnwesen und Einstellungen.
  if (to === '/correspondence')
    return (
      pathname === '/correspondence' ||
      (pathname.startsWith('/correspondence/') &&
        !pathname.startsWith('/correspondence/dunning') &&
        !pathname.startsWith('/correspondence/settings'))
    );
  // AP-35 Nachtrag 5: „Projekte" ist das Detailblatt — nicht aktiv auf Liste und Einstellungen.
  if (to === '/projects')
    return pathname === '/projects' || (pathname.startsWith('/projects/') && pathname !== '/projects/list' && !pathname.startsWith('/projects/settings'));
  // AP-37: „Kalkulationen" bleibt auf einer geöffneten Kalkulation aktiv, nicht aber in
  // Materialanfragen, Vorlagen und Einstellungen — die haben eigene Menüpunkte.
  if (to === '/calculations')
    return (
      pathname === '/calculations' ||
      (pathname.startsWith('/calculations/') && !['/calculations/material-requests', '/calculations/templates', '/calculations/settings'].some((p) => pathname.startsWith(p)))
    );
  // AP-48: „Dokumente" bleibt auf einem geöffneten Dokument aktiv, nicht aber im Bautagebuch;
  // „Bautagebuch" nicht in der Behinderungsliste, die ihren eigenen Menüpunkt hat.
  if (to === '/site-docs') return pathname === '/site-docs' || (pathname.startsWith('/site-docs/') && !pathname.startsWith('/site-docs/diary'));
  if (to === '/site-docs/diary') return pathname === '/site-docs/diary';
  return pathname.startsWith(to);
}

/** Alle Blatt-Einträge einer (ggf. verschachtelten) Gruppe — unabhängig von Sichtbarkeit. */
function collectLeaves(item: NavItem): NavItem[] {
  if (!item.children) return [item];
  return item.children.flatMap(collectLeaves);
}

/** Blatt-Einträge, die für Rolle/Modul/Datenweg sichtbar sind (rekursiv). */
export function visibleLeaves(
  item: NavItem,
  darf: (p: string) => boolean,
  hasModule: (m: string) => boolean,
  navCfg: NavVisibilityCtx,
): NavItem[] {
  if (!isVisible(item, darf, hasModule, navCfg)) return [];
  if (!item.children) return [item];
  return item.children.flatMap((c) => visibleLeaves(c, darf, hasModule, navCfg));
}

/** Steht der Benutzer gerade auf dem Dashboard dieses Bereichs? */
function aufDashboard(item: NavItem, pathname: string): boolean {
  return item.dashboard === true && pathname === item.to;
}

/**
 * Liegt die aktuelle Seite in dieser Gruppe — auf ihrem Dashboard oder auf einem ihrer
 * Unterpunkte (rekursiv über Untergruppen)?
 */
export function isGroupActive(item: NavItem, pathname: string): boolean {
  return aufDashboard(item, pathname) || collectLeaves(item).some((leaf) => matchesPath(leaf.to, pathname));
}

/**
 * AP-18 6. — wie das Menü die Seite nennt, auf der der Benutzer steht („Zeiterfassung ·
 * Fehlzeiten"). Der Assistent bekommt das als Ort mit. Leer auf Seiten, die in keinem Menü stehen.
 *
 * `label` ist `useNavLabel()` — der Titel trägt damit den Begriff des Mandanten (AP-16).
 */
export function seitenTitel(items: NavItem[], pathname: string, label: (key: string) => string): string {
  for (const item of items) {
    if (!item.children) {
      if (item.to === '/' ? pathname === '/' : matchesPath(item.to, pathname)) return label(item.label);
      continue;
    }
    if (!isGroupActive(item, pathname)) continue;
    if (aufDashboard(item, pathname)) return label(item.label);
    // Der genaueste Unterpunkt gewinnt: `/time-tracking/absences` vor `/time-tracking`.
    const blatt = collectLeaves(item)
      .filter((l) => matchesPath(l.to, pathname))
      .sort((a, b) => b.to.length - a.to.length)[0];
    return blatt ? `${label(item.label)} · ${label(blatt.label)}` : label(item.label);
  }
  return '';
}

/**
 * AP-40 — Wohin der Klick auf Symbol oder Namen führt: zum Dashboard, im Übergang
 * (MD-12) zum ersten sichtbaren Unterpunkt, bei Einrichtungsgruppen nirgendwohin.
 */
export function zielDerGruppe(
  item: NavItem,
  darf: (p: string) => boolean,
  hasModule: (m: string) => boolean,
  navCfg: NavVisibilityCtx,
): string | null {
  if (item.dashboard === true) return item.to;
  if (item.dashboard === 'folgt') return visibleLeaves(item, darf, hasModule, navCfg)[0]?.to ?? null;
  return null;
}

// ─── Hover handlers ───────────────────────────────────────────────

export function onNavEnter(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  if (el.getAttribute('aria-current') !== 'page' && !el.dataset.groupActive) {
    el.style.backgroundColor = 'color-mix(in srgb, var(--sidebar-active-bg) 18%, transparent)';
    el.style.color = 'var(--sidebar-active-fg)';
  }
}

export function onNavLeave(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  if (el.getAttribute('aria-current') !== 'page' && !el.dataset.groupActive) {
    el.style.backgroundColor = 'transparent';
    el.style.color = 'var(--sidebar-fg)';
  }
}

const AKTIV = { backgroundColor: 'var(--sidebar-active-bg)', color: 'var(--sidebar-active-fg)' };
const GETOENT = { backgroundColor: 'color-mix(in srgb, var(--sidebar-active-bg) 40%, transparent)', color: 'var(--sidebar-active-fg)' };
const RUHIG = { color: 'var(--sidebar-fg)' };

/** Unterpunkte, deren Pfad der Anfang eines anderen ist — nur bei genauem Pfad hervorgehoben. */
function genau(to: string): boolean {
  return to === '/time-tracking' || to === '/settings' || to === '/integration' || to === '/correspondence';
}

// ─── Sub-components ───────────────────────────────────────────────

/**
 * AP-16 — Beschriftung eines Menüpunkts, mit dem Begriff des Mandanten.
 *
 * Liegt bewusst an einer Stelle: Die Ableitung stand zuerst nur im
 * Gruppen-Renderer, und die flachen Menüpunkte (z. B. das Tagebuch) gingen
 * daran vorbei — sichtbar erst beim Umschalten auf „Auftrag".
 */
export function useNavLabel() {
  const { t } = useTranslation();
  const terms = useTerminology();
  return (key: string) =>
    key === 'nav.projects' ? terms.projectPlural
    : key === 'nav.projectList' ? terms.list
    : key === 'nav.projectManagement' ? terms.management
    : key === 'nav.timeTrackingMasterData'
      // Nur der erste Teil schaltet um; „Tätigkeiten" bleibt.
      ? `${terms.projectPlural} & ${t('nav.activities', { defaultValue: 'Tätigkeiten' })}`
    : t(key);
}

export interface NavGroupProps {
  item: NavItem;
  darf: (p: string) => boolean;
  collapsed: boolean;
  hasModule: (m: string) => boolean;
  navCfg: NavVisibilityCtx;
}

/**
 * Eine Gruppe der Seitenleiste.
 *
 * **AP-40 (Menüführung):** Bei einem Hauptmenüpunkt mit Startseite (`dashboard`) hat der
 * Kopf **zwei** Ziele. Symbol und Name sind ein Verweis zum Dashboard und blenden das
 * Untermenü ein — ein zweiter Klick darauf schließt es **nicht** (MD-2). Der Pfeil ist ein
 * eigener Knopf und blendet nur ein und aus, ohne die Seite zu wechseln. Einrichtungsgruppen
 * bleiben ein einziger Knopf zum Auf- und Zuklappen (MD-1).
 */
export function NavGroup({ item, darf, collapsed, hasModule, navCfg }: NavGroupProps) {
  const tl = useNavLabel();
  const { t } = useTranslation();
  const location = useLocation();
  const active = isGroupActive(item, location.pathname);
  const [open, setOpen] = useState(active);

  // MD-4: Die Gruppe öffnet sich, sobald die Seite in sie wechselt — nicht nur beim ersten
  // Anzeigen. Wer über einen Verweis von anderswo in die Kalkulation springt, sieht ihr
  // Untermenü. Verlässt man die Gruppe, bleibt sie, wie der Benutzer sie gestellt hat.
  useEffect(() => {
    if (active) setOpen(true);
  }, [active, location.pathname]);

  const Icon = item.icon;
  const visibleChildren = item.children?.filter((child) => isVisible(child, darf, hasModule, navCfg)) ?? [];

  if (visibleChildren.length === 0) return null;

  const ziel = zielDerGruppe(item, darf, hasModule, navCfg);
  const kopfStil = aufDashboard(item, location.pathname) ? AKTIV : active ? GETOENT : RUHIG;

  // Eingeklappte Seitenleiste: Eltern-Symbol + alle (rekursiv gesammelten) Blatt-Symbole darunter.
  // MD-3: Das Eltern-Symbol führt zum Dashboard, wie der Name in der ausgeklappten Leiste.
  if (collapsed) {
    const leaves = visibleLeaves(item, darf, hasModule, navCfg).filter((l) => !l.children);
    return (
      <div>
        {ziel ? (
          <NavLink
            to={ziel}
            end
            title={tl(item.label)}
            aria-label={tl(item.label)}
            data-group-active={active ? 'true' : undefined}
            className="flex items-center justify-center rounded-md p-2 transition-colors"
            style={() => (active ? AKTIV : RUHIG)}
            onMouseEnter={onNavEnter}
            onMouseLeave={onNavLeave}
          >
            <Icon size={18} />
          </NavLink>
        ) : (
          <div
            className="flex items-center justify-center rounded-md p-2 transition-colors"
            style={active ? AKTIV : RUHIG}
            title={tl(item.label)}
          >
            <Icon size={18} />
          </div>
        )}

        {/* Blatt-Icons – eingerückt, etwas kleiner */}
        <div className="ml-1 mt-0.5 space-y-0.5 border-l border-[var(--sidebar-border)] pl-1">
          {leaves.map((child) => {
            const ChildIcon = child.icon;
            return (
              <NavLink
                key={child.to}
                to={child.to}
                end={genau(child.to)}
                // Hover-Logik (`onNavEnter`/`onNavLeave`) respektiert die aktive Zeile nach `matchesPath`.
                data-group-active={matchesPath(child.to, location.pathname) ? 'true' : undefined}
                className="flex items-center justify-center rounded-md p-1.5 transition-colors"
                title={tl(child.label)}
                // Dieselbe Regel wie die Gruppe (`matchesPath`): Ein Unterpunkt, dessen Pfad der Anfang
                // eines anderen ist („Belege" vor „Einstellungen"), bleibt sonst immer hervorgehoben.
                style={() => (matchesPath(child.to, location.pathname) ? AKTIV : RUHIG)}
                onMouseEnter={onNavEnter}
                onMouseLeave={onNavLeave}
              >
                <ChildIcon size={14} />
              </NavLink>
            );
          })}
        </div>
      </div>
    );
  }

  const Pfeil = open ? ChevronDown : ChevronRight;

  return (
    <div>
      {ziel ? (
        <div
          className="flex w-full items-center rounded-md text-sm transition-colors"
          style={kopfStil}
          data-group-active={active ? 'true' : undefined}
          onMouseEnter={onNavEnter}
          onMouseLeave={onNavLeave}
        >
          <NavLink
            to={ziel}
            end
            // Der Name öffnet, schließt aber nie (MD-2). Steht man schon auf dem Ziel, bleibt
            // die Seite, und das Untermenü geht auf, falls es zu war.
            onClick={(e) => {
              setOpen(true);
              if (location.pathname === ziel) e.preventDefault();
            }}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-md py-2 pl-3"
            style={{ color: 'inherit' }}
          >
            <Icon size={18} className="flex-shrink-0" />
            <span className="flex-1 truncate text-left">{tl(item.label)}</span>
          </NavLink>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-label={t('nav.toggleSubmenu')}
            title={t('nav.toggleSubmenu')}
            className="mr-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md"
            style={{ color: 'inherit' }}
          >
            <Pfeil size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors"
          style={active ? GETOENT : RUHIG}
          onMouseEnter={onNavEnter}
          onMouseLeave={onNavLeave}
        >
          <Icon size={18} className="flex-shrink-0" />
          <span className="flex-1 text-left">{tl(item.label)}</span>
          <Pfeil size={14} />
        </button>
      )}

      {open && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-[var(--sidebar-border)] pl-2">
          {visibleChildren.map((child) => {
            // Verschachtelte Gruppe → rekursiv als NavGroup rendern
            if (child.children) {
              return (
                <NavGroup
                  key={child.to}
                  item={child}
                  darf={darf}
                  collapsed={false}
                  hasModule={hasModule}
                  navCfg={navCfg}
                />
              );
            }
            const ChildIcon = child.icon;
            return (
              <NavLink
                key={child.to}
                to={child.to}
                end={genau(child.to)}
                // Hover-Logik (`onNavEnter`/`onNavLeave`) respektiert die aktive Zeile nach `matchesPath`.
                data-group-active={matchesPath(child.to, location.pathname) ? 'true' : undefined}
                className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs transition-colors"
                // Dieselbe Regel wie die Gruppe (`matchesPath`): Ein Unterpunkt, dessen Pfad der Anfang
                // eines anderen ist („Belege" vor „Einstellungen"), bleibt sonst immer hervorgehoben.
                style={() => (matchesPath(child.to, location.pathname) ? AKTIV : RUHIG)}
                onMouseEnter={onNavEnter}
                onMouseLeave={onNavLeave}
              >
                <ChildIcon size={14} className="flex-shrink-0" />
                {tl(child.label)}
                {child.freigaben && <FreigabenZahl />}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export interface SingleNavItemProps {
  item: NavItem;
  collapsed: boolean;
}

export function SingleNavItem({ item, collapsed }: SingleNavItemProps) {
  const tl = useNavLabel();
  const Icon = item.icon;
  const [ziel, setZiel] = useState(false);
  const ablegen = usePosteingang((s) => s.ablegen);
  const flaeche = item.posteingang && darfAblegen() ? ablageflaeche(setZiel, (d) => void ablegen(d)) : {};
  // Eine Internetadresse auf „Geschäftspartner": Die Seite prüft und öffnet den Anlage-Dialog.
  const navigate = useNavigate();
  const legeAdresse = useAdressAblage((s) => s.lege);
  const adressFlaeche =
    item.adressAblage && darfAdresseAblegen()
      ? adressAblageflaeche(setZiel, (adresse) => {
          legeAdresse(adresse);
          navigate(item.to);
        })
      : {};

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={`flex items-center ${collapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2'} rounded-md text-sm transition-colors`}
      title={collapsed ? tl(item.label) : undefined}
      style={({ isActive }) => ({ ...(isActive ? AKTIV : RUHIG), ...(ziel ? { outline: '2px solid #38bdf8' } : {}) })}
      onMouseEnter={onNavEnter}
      onMouseLeave={onNavLeave}
      {...flaeche}
      {...adressFlaeche}
    >
      <span className="relative flex-shrink-0">
        <Icon size={18} />
        {item.posteingang && collapsed && <PosteingangZahl klein />}
      </span>
      {!collapsed && tl(item.label)}
      {item.posteingang && !collapsed && <PosteingangZahl />}
    </NavLink>
  );
}
